import React, { useEffect, useState, useMemo } from "react";
import { Pie, Column } from "@ant-design/plots";
import { useWindowWidth } from "../../../../hooks/useWindowWidth";
import { getDonutHeight, BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { kgToTons } from "../../../../utils/dataAndFormulas/GEIForms";
import { formatWhole, formatMagnitudeAdaptive, formatMagnitudeAdaptiveAxis, formatPercentage } from "../../../../utils/numberFormat";
import "./TableGraf.scss";
import { EmptyChartMessage } from "../EmptyChartMessage";

const barPalette = BAR_PALETTE;

const geiColorMapDonut = DONUT_COLORS.gei;

export function buildGEIDonutData(resultadosPorAlcance, year) {
  if (!resultadosPorAlcance?.[year]) return [];

  const yearData = resultadosPorAlcance[year];
  const scopes = ["scope1", "scope2", "scope3"];

  return scopes
    .map((scope) => ({
      type:
        scope === "scope1"
          ? "Alcance 1 - Emisiones directas"
          : scope === "scope2"
          ? "Alcance 2 - Energía comprada"
          : "Alcance 3 - Emisiones indirectas",
      value: yearData[scope] || 0,
    }))
    .filter((item) => item.value > 0);
}

export function buildGEIBarChartData(totales) {
  if (!totales) return [];

  // Obtenemos todos los años únicos de todos los tipos
  const allYears = new Set();
  Object.values(totales).forEach((tipo) => {
    Object.keys(tipo).forEach((year) => allYears.add(year));
  });

  // Para cada año, sumamos todas las emisiones y filtramos los que tienen valor > 0
  return Array.from(allYears)
    .map((year) => {
      const totalEmissions = Object.values(totales).reduce((acc, tipo) => {
        const emisiones = tipo?.[year]?.emissions || 0;
        return acc + emisiones;
      }, 0);

      return {
        year,
        value: kgToTons(totalEmissions),
        unit: "tCO₂e",
        rawValue: kgToTons(totalEmissions),
      };
    })
    .filter((item) => item.rawValue > 0);
}

export function buildGEIBarChartDataIntensity(resultadosPorAlcance, resultsProductions) {
  if (!resultadosPorAlcance || !resultsProductions) return [];

  return Object.entries(resultadosPorAlcance)
    .map(([year, scopes]) => {
      const totalEmisiones =
        (scopes.scope1 || 0) + (scopes.scope2 || 0) + (scopes.scope3 || 0);
      const produccion = resultsProductions[year] || 0;

      const intensidad =
        produccion > 0 && totalEmisiones > 0
          ? totalEmisiones / produccion
          : 0;

      return {
        year,
        value: intensidad,
        unit: "kgCO₂e / unidad producida",
      };
    })
    .filter((item) => item.value > 0 && !isNaN(item.value));
}

const TableGraf = ({ filters, totales, totalesScopes, valueBaseline, t, chartCardRefs }) => {
  const { year } = filters || {};
  const [dataConsumo, setDataConsumo] = useState([]);
  const [data, setdata] = useState([]);
  const windowWidth = useWindowWidth();

  // Memoizar configuración responsive para evitar recalcular en cada render
  const responsiveConfig = useMemo(() => {
    const isMobile = windowWidth <= 768;
    const isSmallMobile = windowWidth <= 480;
    const isTablet = windowWidth > 768 && windowWidth <= 992;
    const isNotebook = windowWidth >= 1024 && windowWidth <= 1500;

    return {
      isMobile,
      isSmallMobile,
      isTablet,
      isNotebook,
      // Tamaños de fuente consistentes
      fontSize: {
        large: isSmallMobile ? 12 : isMobile ? 14 : 16,
        medium: isSmallMobile ? 10 : isMobile ? 11 : 12,
        small: isSmallMobile ? 9 : isMobile ? 10 : 11,
      },
      // Alturas de gráficos
      chartHeight: {
        donut: getDonutHeight(isNotebook, isMobile),
        bar: isSmallMobile ? 250 : isMobile ? 280 : 300,
      },
    };
  }, [windowWidth]);

  useEffect(() => {
    if (!totales || !year) return;

    const costoData = totales;

    setdata(costoData);
  }, [filters, totales, year]);

  const formatValue = (value, unit) => `${formatWhole(value)} ${unit}`;


  /////////////Dona
  const GEIDonut = ({ resultadosPorAlcance, year, t }) => {
    const [hiddenTypes, setHiddenTypes] = useState([]);

    if (!resultadosPorAlcance?.[year]) {
      return (
        <EmptyChartMessage
          message={
            t("complete_at_least_one_period") ||
            "Complete al menos un período para visualizar este indicador"
          }
        />
      );
    }

    const rawData = buildGEIDonutData(resultadosPorAlcance, year);
    const totalGeneral = rawData.reduce((sum, item) => sum + item.value, 0);
    const allData = rawData.map((item) => ({
      ...item,
      percent: totalGeneral ? item.value / totalGeneral : 0,
    }));

    if (!allData.length) {
      return (
        <EmptyChartMessage
          message={
            t("complete_at_least_one_period") ||
            "Complete al menos un período para visualizar este indicador"
          }
        />
      );
    }

    const chartData = allData.filter((d) => !hiddenTypes.includes(d.type));

    const config = {
      data: chartData,
      angleField: "value",
      colorField: "type",
      scale: { color: { domain: Object.keys(geiColorMapDonut), range: Object.values(geiColorMapDonut) } },
      radius: 1,
      innerRadius: 0.6,
      height: responsiveConfig.chartHeight.donut,
      label: false,
      legend: false,
      statistic: {
        title: false,
        content: {
          content: `Total\n${formatValue(kgToTons(totalGeneral), "tCO₂e")}`,
          style: {
            fontSize: responsiveConfig.fontSize.large,
            fontWeight: 300,
            whiteSpace: "pre-line",
          },
        },
      },
      tooltip: {
        title: (datum) => datum.type,
        items: [
          (datum) => {
            const percentage = totalGeneral > 0 ? formatPercentage((datum.value / totalGeneral) * 100) : formatPercentage(0);
            return {
              name: "",
              value: `${formatValue(kgToTons(datum.value), "tCO₂e")} (${percentage})`,
            };
          },
        ],
      },
      interactions: [{ type: "element-active" }],
    };

    return (
      <>
        <div className="chartWrapper">
          <Pie {...config} />
        </div>
        <div
          className="pillsRow"
          style={{ flexDirection: "column", gap: "8px", paddingTop: "2px" }}
        >
          {allData.map((item) => {
            const isHidden = hiddenTypes.includes(item.type);
            const color = geiColorMapDonut[item.type] || "#ccc";
            return (
              <div
                key={item.type}
                className="yearPill"
                onClick={() =>
                  setHiddenTypes((prev) =>
                    isHidden
                      ? prev.filter((g) => g !== item.type)
                      : [...prev, item.type]
                  )
                }
                style={{
                  backgroundColor: isHidden ? "#E5E7EB" : color,
                  textDecoration: isHidden ? "line-through" : "none",
                  width: "100%",
                }}
              >
                {isHidden ? item.type : `${item.type} (${formatPercentage(item.percent * 100)})`}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  /////////////Barras
  const BarChart = ({ data }) => {
    const rawData = buildGEIBarChartData(data);
    const allYears = rawData.map((d) => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const colorMap = {};
    allYears.forEach((y, i) => { colorMap[y] = barPalette[i] || "#ccc"; });

    const maxValue = rawData.length > 0 ? Math.max(...rawData.map((d) => d.rawValue)) : 0;

    const allNormalized = rawData.map((d) => {
      const isSmall = d.rawValue < 1;
      return {
        year: d.year,
        unit: d.unit,
        displayValue: isSmall ? maxValue * 0.1 : d.rawValue,
        realValue: d.rawValue,
        isSmall: isSmall,
      };
    });

    const chartData = allNormalized.filter((d) => activeYears.includes(d.year));

    // console.log("🔍 buildBarChartData - resultado final:", rawData);
    // console.log("📊 Chart Data Emisiones normalizado:", chartData);

    const config = {
      data: chartData,
      xField: "year",
      yField: "displayValue",
      autoFit: true,
      colorField: "year",
      scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
      height: responsiveConfig.chartHeight.bar,
      label: false,
      legend: false,
      tooltip: {
        shared: true,
        title: (d) => `Año ${d.year}`,
        items: [
          (datum) => {
            const value = datum?.realValue ?? datum?.value ?? 0;
            const unit = datum?.unit || "";
            return {
              name: " ",
              value: formatValue(value, unit),
            };
          },
        ],
      },
      // La API de @ant-design/plots v2 (G2 v5) configura los ejes vía
      // `axis: { x, y }`, no `xAxis`/`yAxis` (API vieja de G2Plot v1/v3) —
      // ese prop legacy no aplica `label.formatter`, por eso el eje se veía
      // sin el separador de miles pese a tener la función lista. Mismo
      // patrón que EnergyKPIsDashboard/TableGraf.js y
      // WaterKPIsDashboard/TableGraf.js.
      axis: {
        x: {
          title: t("year"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.medium,
          labelAutoRotate: responsiveConfig.isMobile ? false : true,
          labelAutoHide: false,
        },
        y: {
          title: responsiveConfig.isMobile ? t("total") : t("total_emissions_axis"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.small,
          // Regla 1 (AJUSTES DE MAGNITUDES NUMÉRICAS): espacio fino
          // separador de miles, sin decimales — los ticks del eje son
          // valores redondos generados por la escala del gráfico.
          labelFormatter: (v) => {
            if (responsiveConfig.isMobile && v >= 1000)
              return `${(v / 1000).toFixed(1)}K`;
            return formatWhole(v);
          },
        },
      },
      interactions: [{ type: "active-region" }],
    };

    return (
      <>
        <div className="chartWrapper">
          <Column {...config} />
        </div>
        <div className="pillsRow">
          {allYears.map((year) => (
            <div
              key={year}
              className="yearPill"
              onClick={() =>
                setActiveYears((prev) =>
                  prev.includes(year)
                    ? prev.filter((y) => y !== year)
                    : [...prev, year]
                )
              }
              style={{
                backgroundColor: activeYears.includes(year)
                  ? colorMap[year]
                  : "#E5E7EB",
                textDecoration: activeYears.includes(year)
                  ? "none"
                  : "line-through",
              }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };

  // 🔹 Gráfico de barras para intensidad de emisiones
  const BarChartIntensity = ({ resultadosPorAlcance, resultsProductions }) => {
    const rawData = buildGEIBarChartDataIntensity(
      resultadosPorAlcance,
      resultsProductions
    );
    const allYears = rawData.map((d) => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const colorMap = {};
    allYears.forEach((y, i) => { colorMap[y] = barPalette[i] || "#ccc"; });

    const maxValue = rawData.length > 0 ? Math.max(...rawData.map((d) => d.value)) : 0;
    const SHOULD_SCALE = maxValue < 0.01;
    const SCALE_FACTOR = SHOULD_SCALE ? 100000 : 1;

    const allNormalized = rawData.map((d) => ({
      year: d.year,
      unit: d.unit,
      displayValue: d.value * SCALE_FACTOR,
      realValue: d.value,
    }));

    const chartData = allNormalized.filter((d) => activeYears.includes(d.year));

    const config = {
      data: chartData,
      xField: "year",
      yField: "displayValue",
      autoFit: true,
      colorField: "year",
      scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
      height: responsiveConfig.chartHeight.bar,
      label: false,
      legend: false,
      tooltip: {
        title: { field: "year" },
        items: [
          (datum) => ({
            name: "Intensidad",
            value: formatValue(datum.realValue, datum.unit),
          }),
        ],
      },
      // Ver comentario de BarChart más arriba: `axis: { x, y }` es la API
      // correcta de @ant-design/plots v2, `xAxis`/`yAxis` no formatea.
      axis: {
        x: {
          title: t("year"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.medium,
        },
        y: {
          title: SHOULD_SCALE
            ? t("emissions_intensity_axis_scaled")
            : t("emissions_intensity_axis"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.small,
          // Regla 2+, pero con la variante "eje" (formatMagnitudeAdaptiveAxis):
          // los ticks del gráfico son números redondos y forzarles 2
          // decimales se veía como ceros de más ("1.00", "6.00").
          labelFormatter: (v) => formatMagnitudeAdaptiveAxis(v),
        },
      },
      interactions: [{ type: "active-region" }],
    };

    return (
      <>
        <div className="chartWrapper">
          <Column {...config} />
          {SHOULD_SCALE && (
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
             {/* Nota: los valores del gráfico están escalados (×100.000) para
              facilitar la lectura. Los valores reales se muestran en el tooltip.*/}
            </div>
          )}
        </div>
        <div className="pillsRow">
          {allYears.map((year) => (
            <div
              key={year}
              className="yearPill"
              onClick={() =>
                setActiveYears((prev) =>
                  prev.includes(year)
                    ? prev.filter((y) => y !== year)
                    : [...prev, year]
                )
              }
              style={{
                backgroundColor: activeYears.includes(year)
                  ? colorMap[year]
                  : "#E5E7EB",
                textDecoration: activeYears.includes(year)
                  ? "none"
                  : "line-through",
              }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };



  // ======================
  // 🎨 RENDER
  // ======================

  return (
    <div className="geiDashboardGrid">
      <div className="chartCard" ref={chartCardRefs?.[0]}>
        <h3 className="cardTitle">
          {t("scope_types")}
        </h3>
        <GEIDonut
          resultadosPorAlcance={totalesScopes}
          year={filters.year}
          t={t}
        />
      </div>
      <div className="chartCard" ref={chartCardRefs?.[1]}>
        <h3 className="cardTitle">
          {t("annual_emissions")}
        </h3>
        <BarChart data={data} />
      </div>
      <div className="chartCard" ref={chartCardRefs?.[2]}>
        <h3 className="cardTitle">
          {t("emissions_intensity") }
        </h3>
        <BarChartIntensity
          resultadosPorAlcance={totalesScopes}
          resultsProductions={valueBaseline}
        />
      </div>
    </div>
  );
};

export default TableGraf;
